import { FC, Fragment } from 'react'
import InlineSvg from 'react-inlinesvg'
import { observer } from 'mobx-react-lite'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { PrismicRichText } from '@prismicio/react'
import { ImageField, LinkField } from '@prismicio/client'
import tw from 'twin.macro'
import { Body, Grid, Header, LargeHeader, SubHeader } from '../UI'
import FeatherIcon from '../FeatherIcon'
import Dropdown from '../Dropdown'
import PrismicLinkInternalOrExternal from '../PrismicLinkInternalOrExternal'
import { theme } from '../../../tailwind.config'
import { RichTextFields } from '../../types/prismic'
import { FeatherIconType } from '../../types/icon'

import ctaArrow from '../../images/icons/cta-arrow.svg'
import ctaArrowRed from '../../images/icons/cta-arrow-red.svg'
import { richTextStrongComponent } from '../../utils/richTextComponents'

type Props = {
  header: string
  subhead: RichTextFields
  image: ImageField
  steps: Array<{
    item_header: string
    item_copy: string
    icon: FeatherIconType
    section_header: string
    item_cta_text: string
    item_cta_link: LinkField
  }>
  cta_text?: string
  cta_link?: LinkField
  background_color: 'gb-blue' | 'gb-red' | 'gb-magenta'
  pagePath: string
  pageTitle: string
}

type CtAProps = {
  text: string
  link: LinkField
  pagePath: string
  pageTitle: string
}

const CtA: FC<CtAProps> = ({ text, link, pagePath, pageTitle }) => (
  <PrismicLinkInternalOrExternal
    id="cta-get-started"
    field={link}
    trackEventParams={{
      pageTitle: pageTitle,
      path: pagePath,
      target: 'url' in link ? link.url : '',
      copy: text,
    }}
    cssStyle={tw`bg-white text-gb-red uppercase px-12 py-4 col-span-full font-object text-[13px] line-height[13px] letter-spacing[1px] flex justify-center items-center sm:max-w-[350px] lg:(w-[312px] transition-colors hover:bg-[#E5E5E5])`}
  >
    {text}
    <InlineSvg src={ctaArrowRed} tw="w-6 ml-[5px] mt-[-2px] inline" />
  </PrismicLinkInternalOrExternal>
)

const TakingControl2: FC<Props> = ({
  header,
  subhead,
  image,
  steps,
  cta_text,
  cta_link,
  background_color = 'gb-blue',
  pagePath,
  pageTitle,
}) => {
  const bgColor = theme.extend.colors[background_color]
  const hoverColor = theme.extend.colors[`${background_color}-dark`]

  return (
    <section
      css={[
        { backgroundColor: bgColor },
        tw`pt-[40px] pb-[40px] lg:(pt-[80px] pb-[80px]) text-white font-domaine overflow-hidden relative`,
      ]}
    >
      <Grid tw="col-span-full">
        <div tw="col-span-full mb-[10px] lg:mb-[25px]">
          <h2 css={Body}>{header}</h2>
        </div>
        <div tw="col-span-full lg:col-span-6 h-full flex flex-col">
          <h3 css={[LargeHeader, tw`mb-[18px]`]}>
            <PrismicRichText
              field={subhead.richText}
              components={richTextStrongComponent}
            />
          </h3>
          <div tw="hidden lg:(w-8/12 flex flex-1 items-center py-2)">
            {!!image && (
              <GatsbyImage image={getImage(image)} alt="" tw="z-[1]" />
            )}
          </div>
        </div>
        {steps && (
          <div tw="hidden lg:(block col-span-6 h-full flex flex-col justify-end)">
            {steps.map((el, index) => {
              const isNotFirstAndNoSectionHeader =
                index !== 0 && !steps[index].section_header
              return (
                <div>
                  {el.section_header && (
                    <h4
                      css={[
                        Header,
                        tw`font-object uppercase`,
                        index !== 0 && tw`pt-6`,
                      ]}
                    >
                      {el.section_header}
                    </h4>
                  )}
                  <div
                    css={[
                      tw`py-[32px] pr-[140px] flex justify-start items-start`,
                      isNotFirstAndNoSectionHeader && tw`border-t border-white`,
                    ]}
                    key={`how-it-works-${index}`}
                  >
                    <FeatherIcon
                      icon={el.icon}
                      cssStyle={tw`w-16 mr-2 mt-[34px]`}
                    />
                    <div tw="flex-1">
                      <div tw="mb-1.5 text-left">
                        {`${index + 1 < 10 && '0'}${index + 1}`}
                      </div>
                      <h4 css={[SubHeader, tw`mb-4`]}>{el.item_header}</h4>
                      {el.item_copy && <p>{el.item_copy}</p>}
                      {el.item_cta_text && el.item_cta_link && (
                        <div tw="pt-4">
                          <PrismicLinkInternalOrExternal
                            id="cta-learn-your-risk-2"
                            field={el.item_cta_link}
                            trackEventParams={{
                              pageTitle: pageTitle,
                              path: pagePath,
                              target:
                                'url' in el.item_cta_link
                                  ? el.item_cta_link.url
                                  : '',
                              copy: el.item_cta_text,
                            }}
                            cssStyle={[
                              { '&:hover': { backgroundColor: hoverColor } },
                              tw`uppercase pt-2 pb-1 font-object text-[13px] line-height[13px] letter-spacing[1px] border-b border-white transition-colors`,
                            ]}
                          >
                            {el.item_cta_text}
                            <InlineSvg
                              src={ctaArrow}
                              tw="w-6 ml-[5px] mt-[-2px] inline text-white"
                            />
                          </PrismicLinkInternalOrExternal>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
            {!!cta_text && !!cta_link && (
              <div tw="mt-6">
                <CtA
                  text={cta_text}
                  link={cta_link}
                  pagePath={pagePath}
                  pageTitle={pageTitle}
                />
              </div>
            )}
          </div>
        )}
      </Grid>
      <Grid>
        {/* Steps as Dropdown - Mobile */}
        {steps && (
          <div tw="col-span-full mb-6 lg:hidden">
            {steps.map((el, index) => (
              <Fragment>
                {el.section_header && (
                  <h4 css={[Header, tw`font-object uppercase pt-6`]}>
                    {el.section_header}
                  </h4>
                )}
                <Dropdown
                  key={index}
                  header={el.item_header}
                  content={<p>{el.item_copy}</p>}
                  icon={el.icon}
                  indexString={`${index + 1 < 10 && '0'}${index + 1}`}
                  topBorder={index !== 0 && !el.section_header}
                />
              </Fragment>
            ))}
          </div>
        )}
      </Grid>
      {/* CTA - Mobile */}
      {!!cta_text && !!cta_link && (
        <Grid tw="lg:hidden">
          <CtA
            text={cta_text}
            link={cta_link}
            pagePath={pagePath}
            pageTitle={pageTitle}
          />
        </Grid>
      )}
    </section>
  )
}

export default observer(TakingControl2)
